import { config } from 'lib-shared-backend-config';

import { AppErrorType } from 'app/types/app.types';

/** Represents the base for the app error message action.. */
type AppErrorMessageActionBase<
  TType extends string,
  TArg extends Record<string, unknown>,
> = {
  /** The type of the action. */
  readonly type: TType;
  /** The action label. */
  readonly label: string;
} & TArg;

/** Represents the action of the app error message. */
export type AppErrorMessageAction =
  | AppErrorMessageActionBase<
      'button',
      {
        readonly onClick: (
          event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        ) => void;
      }
    >
  | AppErrorMessageActionBase<
      'link',
      {
        /** The external url to navigate to. */
        readonly url: string;
      }
    >;

/** Represents the app error message. */
export type AppErrorMessage = {
  /** The message title. */
  readonly title: string;
  /** The message description. */
  readonly description: string;
  /** The message actions. */
  readonly actions?: readonly AppErrorMessageAction[];
};

/** Various application error prompts. */
export const appErrors: Readonly<Record<AppErrorType, AppErrorMessage>> = {
  activeOngoingCall: {
    title: 'Active ongoing call',
    description: `Login was prevented because you are active in an ongoing call.\nClose any ongoing calls in other windows to proceed.`,
    actions: [
      {
        type: 'link',
        label: 'Contact us',
        url: `https://${config.domain.web}/contact`,
      },
    ],
  },
  agentInsufficientRoles: {
    title: 'Permission denied',
    description: `Your agent does not have sufficient roles to login at ${window.location.host}`,
    actions: [
      {
        type: 'link',
        label: 'Contact us',
        url: `https://${config.domain.web}/contact`,
      },
    ],
  },
  failedOktaLogin: {
    title: 'Okta login failed',
    description: `Login to ${window.location.host} failed. Please contact your Okta admin.`,
    actions: [
      {
        type: 'link',
        label: 'Contact us',
        url: `https://${config.domain.web}/contact`,
      },
    ],
  },
  oktaHandleRedirect: {
    title: 'Redirecting to Okta',
    description: `Please standby...`,
    actions: [],
  },
  oktaHandleSigninRedirect: {
    title: 'Logging in',
    description: `Please standby...`,
    actions: [],
  },
  noInternetConnection: {
    title: 'Internet connection',
    description: `It seem you don't have an internet connection right now.`,
    actions: [
      {
        type: 'button',
        label: 'Try again',
        onClick: () => window.location.reload(),
      },
    ],
  },
  organizationSuspended: {
    title: 'Suspended',
    description: 'This organization has been suspended.',
    actions: [
      {
        type: 'link',
        label: 'Contact us',
        url: `https://${config.domain.web}/contact`,
      },
    ],
  },
  unsupportedPlatform: {
    title: 'Unsupported platform',
    description: 'Your device does not seem to be supported.',
  },
};
